var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './index.scss';
import { array, bool, func, string } from 'prop-types';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Input from '~/components/input';
import Button from '~/form_components/button';
import { useUser } from '~/providers/user';
const PrivilegeGroups = forwardRef((_a, ref) => {
    var { handlePost, privilegeGroups, setShowPrivilegeList, className } = _a, props = __rest(_a, ["handlePost", "privilegeGroups", "setShowPrivilegeList", "className"]);
    PrivilegeGroups.propTypes = {
        privilegeGroups: array,
        handlePost: func.isRequired,
        setShowPrivilegeList: func.isRequired,
        className: string,
    };
    const [selectedGroups, setSelectedGroups] = useState([]);
    const handleSelect = groupId => {
        const groupIndex = selectedGroups.indexOf(groupId);
        if (groupIndex > -1) {
            const newGroups = [...selectedGroups];
            newGroups.splice(groupIndex, 1);
            setSelectedGroups(newGroups);
            return;
        }
        const newGroups = [...selectedGroups, groupId];
        setSelectedGroups(newGroups);
    };
    const handleClickPost = () => {
        const groups = [...selectedGroups];
        handlePost(groups);
        setSelectedGroups([]);
        setShowPrivilegeList(false);
    };
    return (React.createElement(Popover, Object.assign({ onMouseEnter: () => setShowPrivilegeList(true), onMouseLeave: () => setShowPrivilegeList(false), ref: ref, className: `${className} privilege-popover` }, props),
        React.createElement(Popover.Content, null,
            React.createElement("div", { className: 'privilege-inputs' },
                React.createElement("ul", { className: 'privilege-list' }, privilegeGroups.map(pg => {
                    return (React.createElement("li", { key: `priv-group-${pg.id}` },
                        React.createElement(Input, { type: 'checkbox', label: pg.displayName, checked: selectedGroups.includes(pg.id), id: `checkbox-${pg.name}`, name: `checkbox-${pg.name}`, onChange: () => handleSelect(pg.id) })));
                })),
                React.createElement("div", { className: 'action' },
                    React.createElement(Button, { onClick: handleClickPost, disabled: !selectedGroups.length }, "Post"))))));
});
export const PostCommentButton = ({ handlePost, disabled, taggedUserIds }) => {
    PostCommentButton.propTypes = {
        disabled: bool,
        handlePost: func.isRequired,
        taggedUserIds: array,
    };
    const dropdownRef = useRef(null);
    const { taggableComparisonUsers, privilegeGroups } = useUser();
    let filteredPrivilegeGroups = [...privilegeGroups];
    const { postingComment } = useSelector(state => state.comparisonDocumentsState);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [showPrivilegeList, setShowPrivilegeList] = useState(false);
    if (taggedUserIds.length > 0) {
        filteredPrivilegeGroups = taggableComparisonUsers.reduce((availableGroups, taggableUser) => {
            if (!taggedUserIds.includes(taggableUser.id)) {
                return availableGroups;
            }
            return availableGroups.filter(group => {
                return taggableUser.privilegeGroups.some(privilegeGroup => privilegeGroup.id === group.id);
            });
        }, filteredPrivilegeGroups);
    }
    useEffect(() => {
        if (disabled) {
            setIsDropdownVisible(false);
        }
    }, [disabled]);
    useEffect(() => {
        if (isDropdownVisible) {
            dropdownRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });
        }
    }, [isDropdownVisible]);
    const handleButtonClick = () => {
        if (!postingComment) {
            setIsDropdownVisible(!isDropdownVisible);
        }
    };
    const handleClickPost = (privGroups) => {
        if (postingComment)
            return;
        if (privGroups === undefined)
            privGroups = [];
        handlePost(privGroups);
        setIsDropdownVisible(false);
    };
    const hasMultiplePrivilegeGroups = filteredPrivilegeGroups.length > 1;
    const hasOnePrivilegeGroup = filteredPrivilegeGroups.length === 1;
    const hasPrivilegeGroups = !!(filteredPrivilegeGroups.length);
    return (React.createElement("div", { className: 'post-comment' },
        React.createElement(Button, { className: 'post-comment-btn', secondary: true, disabled: disabled | postingComment, onClick: hasPrivilegeGroups ? handleButtonClick : () => handleClickPost() },
            postingComment && (React.createElement("span", null,
                React.createElement("i", { className: 'fas fa-spinner fa-spin posting' }))),
            !postingComment && (React.createElement(React.Fragment, null,
                React.createElement("p", null, "Post Comment"),
                hasPrivilegeGroups &&
                    React.createElement("div", { className: 'btn-split', key: isDropdownVisible ? 'up-arrow' : 'down-arrow' },
                        React.createElement("span", null,
                            React.createElement("i", { className: isDropdownVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down' })))))),
        isDropdownVisible && hasPrivilegeGroups && (React.createElement("ul", { ref: dropdownRef, className: 'dropdown-actions' },
            React.createElement("li", { title: 'Post Comment Publicly', onClick: () => handleClickPost() }, "Public"),
            hasOnePrivilegeGroup && (React.createElement("li", { title: `Post Comment Restricted to ${filteredPrivilegeGroups[0].displayName}`, onClick: () => handleClickPost([filteredPrivilegeGroups[0].id]) }, filteredPrivilegeGroups[0].displayName)),
            hasMultiplePrivilegeGroups && (React.createElement("li", { title: 'Post Comment With Restricted Privileges' },
                React.createElement(OverlayTrigger, { trigger: ['hover', 'focus'], placement: 'right', show: showPrivilegeList, overlay: React.createElement(PrivilegeGroups, { privilegeGroups: filteredPrivilegeGroups, setShowPrivilegeList: setShowPrivilegeList, handlePost: handleClickPost }) },
                    React.createElement("div", { className: 'trigger', onMouseEnter: () => setShowPrivilegeList(true), onMouseLeave: () => setShowPrivilegeList(false) },
                        React.createElement("span", null, "Privilege"),
                        React.createElement("i", { className: 'fas fa-chevron-right icon' })))))))));
};
